import { getSupabaseClient } from '@/integrations/supabase/client';
import { ProductModifier, ProductModifierIngredient } from '@/types';

export type ProductModifierInput = Omit<
  ProductModifier,
  'id' | 'organizationId' | 'createdAt' | 'updatedAt' | 'ingredients' | 'categoryId'
> & {
  /** Omit to leave the stored recipe untouched. */
  ingredients?: ProductModifierIngredient[];
  /** Omit or null leaves the modifier uncategorized. */
  categoryId?: string | null;
};

type WriteError = { code?: string; message?: string } | null;

/** Org DBs that have not run 35_product_modifier_categories yet have no category_id column. */
const isMissingCategoryColumn = (error: WriteError): boolean => {
  if (!error?.message?.includes('category_id')) return false;
  return error.code === '42703' || error.code === 'PGRST204';
};

const modifierWriteRow = (
  modifier: ProductModifierInput,
  options: { organizationId?: string; updatedAt?: string; includeCategory: boolean }
) => {
  const row: {
    name: string;
    type?: string;
    notes?: string | null;
    price: number;
    organization_id?: string;
    updated_at?: string;
    category_id?: string | null;
  } = {
    name: modifier.name,
    type: modifier.type,
    notes: modifier.notes,
    price: modifier.price ?? 0,
  };
  if (options.organizationId) row.organization_id = options.organizationId;
  if (options.updatedAt) row.updated_at = options.updatedAt;
  if (options.includeCategory) row.category_id = modifier.categoryId ?? null;
  return row;
};

const PRODUCT_MODIFIERS_SELECT = `
  *,
  product_modifier_ingredients (
    ingredient_id,
    quantity
  )
`;

type ProductModifierIngredientRow = { ingredient_id: string; quantity: number | string };

const mapIngredientRows = (
  rows: ProductModifierIngredientRow[] | null | undefined
): ProductModifierIngredient[] =>
  (rows ?? [])
    .filter((row) => row != null)
    .map((row) => ({
      ingredientId: row.ingredient_id,
      quantity: Number(row.quantity),
    }));

/** Persisted cache from before priced modifiers may omit `price` / `ingredients`. */
export const normalizeProductModifier = (modifier: ProductModifier): ProductModifier => ({
  ...modifier,
  price: Number(modifier.price) || 0,
  ingredients: modifier.ingredients ?? [],
  categoryId: modifier.categoryId ?? null,
});

async function setModifierIngredients(
  modifierId: string,
  organizationId: string,
  ingredients: ProductModifierIngredient[]
) {
  const supabase = getSupabaseClient();
  const { error: deleteError } = await supabase
    .from('product_modifier_ingredients')
    .delete()
    .eq('modifier_id', modifierId);

  if (deleteError) throw deleteError;

  const rows = ingredients
    .filter((ingredient) => ingredient.quantity > 0)
    .map((ingredient) => ({
      modifier_id: modifierId,
      ingredient_id: ingredient.ingredientId,
      quantity: ingredient.quantity,
      organization_id: organizationId,
    }));
  if (rows.length === 0) return;

  const { error: insertError } = await supabase.from('product_modifier_ingredients').insert(rows);
  if (insertError) throw insertError;
}

export const productModifiersService = {

  async getProductModifiers(organizationId: string) {
    const supabase = getSupabaseClient();
    const mapRows = (data: Array<{
      id: string;
      name: string;
      type?: string | null;
      notes?: string | null;
      price?: number | string | null;
      category_id?: string | null;
      organization_id: string;
      created_at: string;
      updated_at: string;
      product_modifier_ingredients?: ProductModifierIngredientRow[] | null;
    }>): ProductModifier[] =>
      data.map((modifier) =>
        normalizeProductModifier({
          id: modifier.id,
          name: modifier.name,
          type: modifier.type || undefined,
          notes: modifier.notes || undefined,
          price: Number(modifier.price ?? 0),
          ingredients: mapIngredientRows(modifier.product_modifier_ingredients),
          categoryId: modifier.category_id ?? null,
          organizationId: modifier.organization_id,
          createdAt: new Date(modifier.created_at),
          updatedAt: new Date(modifier.updated_at),
        })
      );

    const withRecipes = await supabase
      .from('product_modifiers')
      .select(PRODUCT_MODIFIERS_SELECT)
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });

    if (!withRecipes.error) {
      return mapRows(withRecipes.data ?? []);
    }

    // Org DBs that have not run 34_priced_product_modifiers yet have no nested table.
    const withoutRecipes = await supabase
      .from('product_modifiers')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });

    if (withoutRecipes.error) throw withoutRecipes.error;
    return mapRows(withoutRecipes.data ?? []);
  },

  async createProductModifier(modifier: ProductModifierInput, organizationId: string) {
    const supabase = getSupabaseClient();
    const insertModifier = (includeCategory: boolean) =>
      supabase
        .from('product_modifiers')
        .insert(modifierWriteRow(modifier, { organizationId, includeCategory }))
        .select()
        .single();

    const inserted = await insertModifier(true);
    let data = inserted.data;
    if (inserted.error) {
      if (!isMissingCategoryColumn(inserted.error)) throw inserted.error;
      const retry = await insertModifier(false);
      if (retry.error) throw retry.error;
      data = retry.data;
    }

    if (modifier.ingredients?.length) {
      await setModifierIngredients(data.id, organizationId, modifier.ingredients);
    }
    return data;
  },

  async updateProductModifier(modifier: ProductModifierInput & Pick<ProductModifier, 'id' | 'organizationId'>) {
    const supabase = getSupabaseClient();
    const updatedAt = new Date().toISOString();
    const updateModifier = (includeCategory: boolean) =>
      supabase
        .from('product_modifiers')
        .update(modifierWriteRow(modifier, { updatedAt, includeCategory }))
        .eq('id', modifier.id);

    const updated = await updateModifier(true);
    if (updated.error) {
      if (!isMissingCategoryColumn(updated.error)) throw updated.error;
      const retry = await updateModifier(false);
      if (retry.error) throw retry.error;
    }

    if (modifier.ingredients) {
      await setModifierIngredients(modifier.id, modifier.organizationId, modifier.ingredients);
    }
  },

  async deleteProductModifier(modifierId: string) {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from('product_modifiers')
      .delete()
      .eq('id', modifierId);

    if (error) throw error;
  },

  async getExistingModifierIds(modifierIds: string[]): Promise<Set<string>> {
    const uniqueIds = [...new Set(modifierIds.filter(Boolean))];
    if (uniqueIds.length === 0) return new Set();

    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('product_modifiers')
      .select('id')
      .in('id', uniqueIds);

    if (error) throw error;
    return new Set((data ?? []).map((row) => row.id as string));
  },

  /** Current ingredient recipes keyed by modifier id (modifiers without a recipe are absent). */
  async getIngredientsByModifierIds(
    modifierIds: string[]
  ): Promise<Map<string, ProductModifierIngredient[]>> {
    const recipes = new Map<string, ProductModifierIngredient[]>();
    const uniqueIds = [...new Set(modifierIds.filter(Boolean))];
    if (uniqueIds.length === 0) return recipes;

    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('product_modifier_ingredients')
      .select('modifier_id, ingredient_id, quantity')
      .in('modifier_id', uniqueIds);

    if (error) throw error;

    for (const row of data ?? []) {
      const list = recipes.get(row.modifier_id as string) ?? [];
      list.push({ ingredientId: row.ingredient_id as string, quantity: Number(row.quantity) });
      recipes.set(row.modifier_id as string, list);
    }
    return recipes;
  },
};
